import { Alert, AppState, Linking, Platform, Share } from 'react-native';
import { addDoc, arrayRemove, arrayUnion, collection, deleteDoc, doc, getDoc, serverTimestamp, setDoc, Timestamp } from 'firebase/firestore';
import { auth, DEFAULT_AVATAR, DEFAULT_COVER, FIRESTORE_COLLECTIONS, firestore, PUBLIC_ACTIVITY_COLLECTIONS } from '../firebase';
import type { TonyteActivity, TonyteUser } from '../types';
import { resolveActivityImage } from './activityImages';
import { buildShareActionId, SHARE_TYPES, trackEventCalendarAdd, trackEventShare } from './eventAnalytics';
import { getResolvedEventId } from './eventIds';
import { getEventCoordinates } from './eventEligibility';
import { normalizeUserRole } from './roles';
import { buildTonyteEventLink, buildTonyteEventPath } from './shareLinks';
import { calculateAge } from './age';
import { publicBio } from './userProfiles';
import { isEventExpired } from './activityDates';
import { assertSocialWriteAllowed, requireVerifiedEmailForAction } from './verificationGate';
import { showTonyteToast } from './tonyteToast';
import { showTonyteConfirmation } from './tonyteConfirmation';

type Translate = (key: string, fallback?: string) => string;

const translateOr = (translate: Translate | undefined, key: string, fallback: string) =>
  translate?.(key, fallback) ?? fallback;

// Single reusable wrapper around the native share sheet for EVERY
// external-event-share entry point in the app (there are currently two
// independent call sites that both end up here: shareActivityExternally
// below, and useVenueVibes.ts's sharePost) — so completion-detection and
// tracking logic lives in exactly one place.
//
// react-native's Share.share() resolves with action:'sharedAction' when the
// user actually completes a share (iOS distinguishes this from
// 'dismissedAction' when they back out of the sheet) — or ALWAYS on
// Android, since Android's share-sheet intent gives the calling app no
// reliable way to know whether the user completed sending inside the
// target app (WhatsApp/Instagram/Messages/etc.) versus backed out of it
// after picking it. The promise resolving at all (rather than throwing) is
// the strongest completion signal the Android platform exposes here — so
// on Android this counts "opened a target app to send this", not a
// guarantee the send itself completed inside that app. This is a platform
// limitation, not something fixable client-side on this RN Share API.
export async function shareEventExternally(
  activity: TonyteActivity,
  content: { title?: string; message: string; url?: string },
): Promise<{ action: string; activityType?: string | null }> {
  const eventId = getResolvedEventId(activity);
  const result = await Share.share(content);
  if (result.action !== 'sharedAction') return result; // iOS dismissedAction — not counted

  trackEventShare({
    eventId,
    organizerId: activity.hostId || null,
    shareType: SHARE_TYPES.EXTERNAL,
    shareActionId: buildShareActionId(eventId, 'external'),
  });
  return result;
}

export const buildActivityInterestDocId = (activityId: string, userId: string) => `${activityId}__${userId}`;
export { buildTonyteEventPath };
export const getEventShareLink = (activity: TonyteActivity) =>
  buildTonyteEventLink(getResolvedEventId(activity));

const firstString = (...values: unknown[]) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
};

export const normalizeCityKey = (city?: string | null) =>
  (city || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

export const getActivityVenueLabel = (activity: TonyteActivity) =>
  firstString(activity.venueName, activity.clubName, activity.locationName, activity.hostName);

export const getActivityPrimaryLink = (activity: TonyteActivity) =>
  firstString(activity.officialLink, activity.sourceLinks?.eventPage, activity.sourceLinks?.website, activity.sourceLinks?.instagram);

export const getLocalizedActivityTime = (
  activity: TonyteActivity,
  locale = 'en-US',
  dateTba = 'Date TBA',
  timeTba = 'Time TBA',
) => {
  const startDate = activity.startDate ? new Date(`${activity.startDate}T12:00:00`) : null;
  const dateLabel = startDate && !Number.isNaN(startDate.getTime())
    ? startDate.toLocaleDateString(locale, { weekday: 'short', month: 'short', day: 'numeric' })
    : activity.startDate || dateTba;
  const timeLabel = activity.startTime
    ? `${activity.startTime}${activity.endTime ? ` - ${activity.endTime}` : ''}`
    : timeTba;
  return `${dateLabel} · ${timeLabel}`;
};

export const formatExternalEventShareText = (
  activity: TonyteActivity,
  translate?: Translate,
  locale = 'en-US',
) => {
  const lines = [
    translateOr(translate, 'event.shareIntro', 'Join this event on Tonyte 🎉'),
    getEventShareLink(activity),
    activity.title,
    getLocalizedActivityTime(
      activity,
      locale,
      translateOr(translate, 'event.dateTba', 'Date TBA'),
      translateOr(translate, 'event.timeTba', 'Time TBA'),
    ),
    getActivityVenueLabel(activity) || activity.address || activity.city,
    activity.description?.trim() || translateOr(translate, 'event.foundOnTonyte', 'Found this on Tonyte.'),
  ].filter(Boolean);
  return lines.join('\n');
};

export async function shareActivityExternally(
  activity: TonyteActivity,
  translate?: Translate,
  locale = 'en-US',
) {
  const link = getEventShareLink(activity);
  if (!link) {
    Alert.alert(
      translateOr(translate, 'event.couldNotShare', 'Could not share'),
      translateOr(translate, 'event.missingShareableId', 'This event is missing a shareable ID.'),
    );
    return;
  }
  try {
    await shareEventExternally(activity, {
      title: activity.title,
      message: formatExternalEventShareText(activity, translate, locale),
      url: link,
    });
  } catch (error) {
    console.warn('[Tonyte][RN][Share] native share failed', {
      activityId: activity.id,
      action: 'Share.share',
      error,
    });
    Alert.alert(
      translateOr(translate, 'event.couldNotShare', 'Could not share'),
      translateOr(translate, 'event.nativeShareUnavailable', 'The native share sheet is unavailable right now.'),
    );
  }
}

// Human-readable "where is this" query — venue name + street address is the
// most specific combination real event data actually has (bare address
// alone can be ambiguous city-to-city; venue alone can match multiple
// branches of a chain). Falls back progressively down to just the city so a
// partially-filled event still opens *something* useful instead of nothing.
const buildMapsSearchQuery = (activity: TonyteActivity): string =>
  firstString(
    [getActivityVenueLabel(activity), activity.address].filter(Boolean).join(', '),
    activity.address,
    [activity.locationName, activity.city].filter(Boolean).join(', '),
    getActivityVenueLabel(activity),
    activity.city,
  );

// Universal HTTPS deep link — works whether or not the Google Maps app is
// installed (Safari/Chrome opens the web result otherwise), so it never
// needs a Linking.canOpenURL() gate. Coordinates are preferred when the
// canonical resolver (getEventCoordinates, shared with map-eligibility
// logic) can find them; otherwise falls back to an encoded venue/address
// text query. Returns '' only when the event has neither.
export const buildGoogleMapsUrl = (activity: TonyteActivity): string => {
  const coords = getEventCoordinates(activity);
  if (coords) {
    return `https://www.google.com/maps/search/?api=1&query=${coords.latitude},${coords.longitude}`;
  }
  const query = buildMapsSearchQuery(activity);
  return query ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}` : '';
};

// Apple's own universal HTTPS Maps link (maps.apple.com) — like the Google
// one above, this opens fine via Safari with no Apple Maps entitlement or
// canOpenURL check needed; iOS just hands https:// links to whichever app
// (or Safari) is registered for them.
const buildAppleMapsUrl = (activity: TonyteActivity): string => {
  const coords = getEventCoordinates(activity);
  const query = buildMapsSearchQuery(activity);
  if (coords) {
    return `https://maps.apple.com/?ll=${coords.latitude},${coords.longitude}${query ? `&q=${encodeURIComponent(query)}` : ''}`;
  }
  return query ? `https://maps.apple.com/?q=${encodeURIComponent(query)}` : '';
};

export type MapsProvider = 'apple' | 'google';

// Single entry point for every "open this event's location in Maps" button
// in the app (previously EventDetailScreen had three separate call sites all
// gated on Linking.canOpenURL(), which iOS/Android can report false for a
// plain https:// URL for reasons that have nothing to do with whether the
// link would actually open — e.g. no maps app has claimed the associated
// domain on that device. A false canOpenURL() result is not a real failure:
// every URL built here is a universal https:// link, which the OS can
// always hand to the default browser at minimum, so this calls
// Linking.openURL() directly and only alerts if that itself throws for
// every candidate in the fallback chain, or there's no usable location data
// on the event at all.
export async function openLocationInMaps(
  activity: TonyteActivity,
  options: { provider?: MapsProvider; fallbackTitle?: string; translate?: Translate } = {},
) {
  const { provider = 'google', fallbackTitle = 'Could not open maps', translate } = options;

  const candidates: string[] = [];
  if (provider === 'apple' && Platform.OS === 'ios') {
    const apple = buildAppleMapsUrl(activity);
    if (apple) candidates.push(apple);
  }
  const google = buildGoogleMapsUrl(activity);
  if (google && !candidates.includes(google)) candidates.push(google);

  if (!candidates.length) {
    Alert.alert(fallbackTitle, translateOr(translate, 'event.missingLocation', 'This event does not have a location yet.'));
    return;
  }

  for (const url of candidates) {
    try {
      await Linking.openURL(url);
      return;
    } catch (error) {
      console.warn('[Tonyte][RN][Linking] maps url failed, trying next fallback', { url, provider, activityId: activity.id, error });
    }
  }

  Alert.alert(fallbackTitle, translateOr(translate, 'common.tryAgain', 'Please try again.'));
}

const toGoogleCalendarDate = (date: Date) =>
  date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');

export const buildGoogleCalendarUrl = (activity: TonyteActivity) => {
  const start = new Date(`${activity.startDate}T${activity.startTime || '00:00'}`);
  const fallbackStart = Number.isNaN(start.getTime()) ? new Date() : start;
  const end = new Date(`${activity.endDate || activity.startDate}T${activity.endTime || activity.startTime || '23:59'}`);
  const fallbackEnd = !Number.isNaN(end.getTime()) && end.getTime() > fallbackStart.getTime()
    ? end
    : new Date(fallbackStart.getTime() + 2 * 60 * 60 * 1000);
  const details = [activity.description, getEventShareLink(activity)].filter(Boolean).join('\n\n');
  const location = [getActivityVenueLabel(activity), activity.address || activity.city].filter(Boolean).join(', ');
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: activity.title,
    dates: `${toGoogleCalendarDate(fallbackStart)}/${toGoogleCalendarDate(fallbackEnd)}`,
    details,
    location,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

export async function openExternalUrl(
  url: string,
  fallbackTitle = 'Could not open link',
  translate?: Translate,
) {
  if (!url) {
    Alert.alert(
      fallbackTitle,
      translateOr(translate, 'event.missingExternalLink', 'This event does not include a link yet.'),
    );
    return;
  }
  try {
    const supported = await Linking.canOpenURL(url);
    if (!supported) throw new Error('Linking.canOpenURL returned false');
    await Linking.openURL(url);
  } catch (error) {
    console.warn('[Tonyte][RN][Linking] open failed', { url, action: 'Linking.openURL', error });
    Alert.alert(fallbackTitle, translateOr(translate, 'common.tryAgain', 'Please try again.'));
  }
}

export async function loadCurrentTonyteUser(uid: string): Promise<TonyteUser> {
  const authUser = auth.currentUser;
  const path = `${FIRESTORE_COLLECTIONS.users}/${uid}`;
  try {
    const snapshot = await getDoc(doc(firestore, FIRESTORE_COLLECTIONS.users, uid));
    const data = snapshot.exists() ? snapshot.data() : {};
    return {
      id: uid,
      name: firstString(data.username, data.displayName, data.name, authUser?.displayName) || 'Tonyte Traveler',
      email: firstString(data.email, authUser?.email),
      photo: firstString(data.profilePhotoURL, data.profileImage, data.photoURL, data.photo, authUser?.photoURL) || DEFAULT_AVATAR,
      coverPhoto: firstString(data.coverPhoto, data.coverImage, data.coverPhotoURL) || DEFAULT_COVER,
      bio: publicBio(data.bio),
      city: firstString(data.locationCity, data.city, data.selectedEventCity),
      country: firstString(data.locationCountry, data.country),
      flag: firstString(data.flag) || 'world',
      age: typeof data.dateOfBirth === 'string' && data.dateOfBirth
        ? calculateAge(data.dateOfBirth)
        : typeof data.age === 'number' ? data.age : 0,
      dateOfBirth: typeof data.dateOfBirth === 'string' ? data.dateOfBirth : undefined,
      interests: Array.isArray(data.interests) ? data.interests : [],
      locationLabel: firstString(data.locationLabel),
      role: normalizeUserRole(data.role),
      isHost: Boolean(data.isHost || data.role === 'organizer' || data.role === 'admin' || data.role === 'co-admin'),
    };
  } catch (error) {
    console.warn('[Tonyte][RN][User] profile read failed', { path, action: 'getDoc', error });
    return {
      id: uid,
      name: authUser?.displayName || 'Tonyte Traveler',
      email: authUser?.email || '',
      photo: authUser?.photoURL || DEFAULT_AVATAR,
      coverPhoto: DEFAULT_COVER,
      bio: '',
      city: '',
      country: '',
      flag: 'world',
      age: 0,
      interests: [],
      locationLabel: '',
      role: 'member',
      isHost: false,
    };
  }
}

export const buildInterestedUserPayload = (currentUser: Pick<TonyteUser, 'id' | 'name' | 'photo'>) => ({
  userId: currentUser.id,
  displayName: currentUser.name || 'Tonyte Traveler',
  photoURL: currentUser.photo || DEFAULT_AVATAR,
  createdAt: Timestamp.now(),
});

export const buildEventParticipantPayload = (currentUser: Pick<TonyteUser, 'id' | 'name' | 'photo'>) => ({
  userId: currentUser.id,
  displayName: currentUser.name || 'Tonyte Traveler',
  photoURL: currentUser.photo || DEFAULT_AVATAR,
  username: currentUser.name || '',
  isVibeVerified: false,
  isStudentVerified: false,
  joinedAt: serverTimestamp(),
  updatedAt: serverTimestamp(),
});

/**
 * Posts a "joined" / "left" notice into an event chat. Written as a normal
 * message doc by the member themselves (rules require senderId == auth uid and
 * current membership, so a "left" notice must go out before memberIds drops
 * them). Deliberately does NOT touch the chat doc's lastMessage/unread counts,
 * and the chat push function skips type 'member_event'. `text` is the fallback
 * older app builds render as a plain bubble. Best-effort: never blocks the
 * join/leave itself.
 */
export async function postEventChatMemberNotice(
  chatId: string,
  userId: string,
  memberEvent: 'joined' | 'left',
  senderName?: string,
) {
  try {
    const name = senderName || (await loadCurrentTonyteUser(userId)).name;
    await addDoc(collection(firestore, FIRESTORE_COLLECTIONS.eventChats, chatId, 'messages'), {
      type: 'member_event',
      memberEvent,
      text: memberEvent === 'joined' ? 'joined the chat' : 'left the chat',
      senderId: userId,
      senderName: name,
      createdAt: serverTimestamp(),
    });
  } catch (error) {
    console.warn('[Tonyte][RN][EventChat] member notice failed', { chatId, memberEvent, error });
  }
}

export async function removeUserFromEventChat(activityId: string, userId: string) {
  const path = `${FIRESTORE_COLLECTIONS.eventChats}/${activityId}`;
  try {
    const chatRef = doc(firestore, FIRESTORE_COLLECTIONS.eventChats, activityId);
    const chatSnapshot = await getDoc(chatRef);
    if (chatSnapshot.exists()) {
      const memberIds = chatSnapshot.data().memberIds || [];
      if (Array.isArray(memberIds) && memberIds.includes(userId)) {
        await postEventChatMemberNotice(activityId, userId, 'left');
        await setDoc(chatRef, { memberIds: arrayRemove(userId) }, { merge: true });
      }
    }
    await deleteDoc(doc(firestore, FIRESTORE_COLLECTIONS.eventChats, activityId, 'participants', userId));
  } catch (error) {
    console.warn('[Tonyte][RN][EventChat] remove failed', { path, action: 'setDoc arrayRemove(memberIds)/delete participant', error });
    throw error;
  }
}

export async function isEventChatMember(activityId: string, userId: string): Promise<boolean> {
  if (!activityId || !userId) return false;
  const chatSnapshot = await getDoc(doc(firestore, FIRESTORE_COLLECTIONS.eventChats, activityId));
  if (!chatSnapshot.exists()) return false;
  const memberIds = chatSnapshot.data().memberIds;
  return Array.isArray(memberIds) && memberIds.includes(userId);
}

/** Resolves only after the user explicitly confirms removing their interest. */
export async function confirmRemoveEventInterest(
  activity: TonyteActivity,
  userId: string,
  translate?: Translate,
): Promise<boolean> {
  let chatMember: boolean;
  try {
    chatMember = await isEventChatMember(getResolvedEventId(activity), userId);
  } catch (error) {
    console.warn('[Tonyte][RN][Interest] membership check failed', { activityId: activity.id, userId, error });
    Alert.alert(
      translateOr(translate, 'event.couldNotUpdateInterest', 'Could not update interest'),
      translateOr(translate, 'common.tryAgain', 'Please try again.'),
    );
    return false;
  }

  return showTonyteConfirmation({
    title: chatMember
      ? translateOr(translate, 'event.leaveThisEvent', 'Leave this event?')
      : translateOr(translate, 'event.noLongerInterested', 'No longer interested?'),
    message: chatMember
      ? translateOr(translate, 'event.removeInterestLeavesChat', 'You’re also part of this event’s chat. Marking the event as not interested will remove you from the event chat as well.')
      : translateOr(translate, 'event.removeInterestConfirm', 'Are you sure you want to mark this event as not interested?'),
    cancelLabel: chatMember
      ? translateOr(translate, 'event.stayInterested', 'Stay interested')
      : translateOr(translate, 'event.keepInterested', 'Keep interested'),
    confirmLabel: chatMember
      ? translateOr(translate, 'event.removeInterestAndLeaveChat', 'Remove interest & leave chat')
      : translateOr(translate, 'event.removeInterest', 'Remove interest'),
    icon: chatMember ? 'log-out' : 'heart',
  });
}

export async function setActivityInterested(
  activity: TonyteActivity,
  userId: string,
  nextInterested: boolean,
  translate?: Translate,
) {
  if (!userId) {
    Alert.alert(
      translateOr(translate, 'auth.signInRequired', 'Sign in required'),
      translateOr(translate, 'auth.signInToUseFeature', 'Sign in to use this feature.'),
    );
    return;
  }

  const resolvedEventId = getResolvedEventId(activity);
  // `events/{id}/interestedUsers` is a REAL Firestore subcollection nested
  // under a document in one of PUBLIC_ACTIVITY_COLLECTIONS ('activities',
  // 'officialEvents', 'events' — see firebase.ts) — unlike activityInterests
  // (a flat top-level collection keyed however the app likes), its security
  // rule does `get(events/$(eventId)).data` to check city visibility, which
  // requires that path to be an actual existing document. Two independent
  // mismatches were compounding here, confirmed live one at a time:
  //   1. resolvedEventId (getResolvedEventId's canonicalEventId/eventId
  //      -field-preferring result) is a stable cross-source identity, not
  //      necessarily the event's own Firestore document ID — fixed by using
  //      activity.id (always the literal snap.id the event was fetched
  //      under) instead, below.
  //   2. Even with the right ID, the write still failed for "OFFICIAL EVENT"
  //      -badged activities: this code hard-coded the collection as the
  //      literal string 'events', but normalizeActivity() already records
  //      which of the three PUBLIC_ACTIVITY_COLLECTIONS an activity actually
  //      came from on `activity.collection` — an event fetched from
  //      officialEvents has no document at all under events/{sameId}, so
  //      get(events/{id}) found nothing and the rule denied the write
  //      regardless of which ID was used.
  // activityInterests/users-interestedEvents keep using resolvedEventId
  // unchanged, since neither has this parent-existence
  // requirement and both already work correctly today.
  const eventDocId = activity.id;
  // Falls back to 'events' for any caller that predates the `collection`
  // field (normalizeActivity has always set it, but a hand-built activity
  // object from an older code path might not) — matches this write's
  // previous hard-coded behavior exactly for that one case, while fixing it
  // for the other two collections.
  const eventDocCollection = activity.collection || 'events';
  const interestPath = `${FIRESTORE_COLLECTIONS.activityInterests}/${buildActivityInterestDocId(resolvedEventId, userId)}`;
  const userInterestPath = `${FIRESTORE_COLLECTIONS.users}/${userId}/interestedEvents/${resolvedEventId}`;
  const eventMirrorPath = `${eventDocCollection}/${eventDocId}/interestedUsers/${userId}`;

  try {
    if (nextInterested) {
      const currentUser = await loadCurrentTonyteUser(userId);
      const payload = {
        activityId: resolvedEventId,
        activityTitle: activity.title,
        cityKey: activity.cityKey || normalizeCityKey(activity.city),
        ...buildInterestedUserPayload(currentUser),
      };
      await setDoc(doc(firestore, FIRESTORE_COLLECTIONS.activityInterests, buildActivityInterestDocId(resolvedEventId, userId)), payload, { merge: true });
      setDoc(doc(firestore, FIRESTORE_COLLECTIONS.users, userId, 'interestedEvents', resolvedEventId), {
        ...payload,
        eventId: resolvedEventId,
        updatedAt: serverTimestamp(),
      }, { merge: true }).catch(error => console.warn('[Tonyte][RN][Interest] user interested event mirror write failed', {
        path: userInterestPath,
        action: 'setDoc',
        error,
      }));
      await setDoc(doc(firestore, eventDocCollection, eventDocId, 'interestedUsers', userId), buildInterestedUserPayload(currentUser), { merge: true })
        .catch(error => console.warn('[Tonyte][RN][Interest] mirrored interested user write failed', {
          path: eventMirrorPath,
          action: 'setDoc',
          error,
        }));
      return;
    }

    // Leave first. If this fails, interest remains and the invariant is preserved.
    await removeUserFromEventChat(resolvedEventId, userId);
    await deleteDoc(doc(firestore, FIRESTORE_COLLECTIONS.activityInterests, buildActivityInterestDocId(resolvedEventId, userId)));
    await deleteDoc(doc(firestore, FIRESTORE_COLLECTIONS.users, userId, 'interestedEvents', resolvedEventId))
      .catch(error => console.warn('[Tonyte][RN][Interest] user interested event delete failed', {
        path: userInterestPath,
        action: 'deleteDoc',
        error,
      }));
    await deleteDoc(doc(firestore, eventDocCollection, eventDocId, 'interestedUsers', userId))
      .catch(error => console.warn('[Tonyte][RN][Interest] mirrored interested user delete failed', {
        path: eventMirrorPath,
        action: 'deleteDoc',
        error,
      }));
  } catch (error) {
    console.warn('[Tonyte][RN][Interest] toggle failed', {
      path: nextInterested ? `${interestPath}, ${eventMirrorPath}` : `${interestPath}, ${eventMirrorPath}`,
      action: nextInterested ? 'setDoc' : 'deleteDoc',
      activityId: resolvedEventId,
      userId,
      nextInterested,
      error,
    });
    Alert.alert(
      translateOr(translate, 'event.couldNotUpdateInterest', 'Could not update interest'),
      translateOr(translate, 'common.tryAgain', 'Please try again.'),
    );
    throw error;
  }
}

export async function getActivityChatPhoto(activity: TonyteActivity) {
  try {
    const resolved = await resolveActivityImage(activity);
    return typeof resolved.source === 'string' ? resolved.source : DEFAULT_COVER;
  } catch {
    return DEFAULT_COVER;
  }
}

export class EventChatExpiredError extends Error {
  readonly code = 'event-chat-expired';

  constructor() {
    super('Event chat expired');
    this.name = 'EventChatExpiredError';
  }
}

export function isEventChatExpiredError(error: unknown): error is EventChatExpiredError {
  return error instanceof EventChatExpiredError
    || (typeof error === 'object' && error !== null && (error as { code?: unknown }).code === 'event-chat-expired');
}

export async function joinEventChat(activity: TonyteActivity, userId: string) {
  if (!userId) {
    throw new Error('Sign in to join event chats.');
  }
  assertSocialWriteAllowed();
  if (isEventExpired(activity)) {
    throw new EventChatExpiredError();
  }
  const resolvedEventId = getResolvedEventId(activity);
  const path = `${FIRESTORE_COLLECTIONS.eventChats}/${resolvedEventId}`;
  const chatRef = doc(firestore, FIRESTORE_COLLECTIONS.eventChats, resolvedEventId);
  const photo = await getActivityChatPhoto(activity);
  const basePayload = {
    eventId: resolvedEventId,
    eventTitle: activity.title,
    eventDescription: activity.description || '',
    eventImage: photo,
    cityKey: activity.cityKey || normalizeCityKey(activity.city),
    photo,
  };
  const now = new Date().toISOString();

  try {
    const currentUser = await loadCurrentTonyteUser(userId);
    const chatSnapshot = await getDoc(chatRef);
    const wasAlreadyMember = chatSnapshot.exists()
      && Array.isArray(chatSnapshot.data().memberIds)
      && chatSnapshot.data().memberIds.includes(userId);
    if (!chatSnapshot.exists()) {
      await setDoc(chatRef, {
        ...basePayload,
        createdAt: now,
        memberIds: [userId],
        lastMessage: '',
        lastMessageAt: now,
        lastMessageSenderId: '',
        lastMessageSenderName: '',
      });
    } else {
      await setDoc(chatRef, { ...basePayload, memberIds: arrayUnion(userId) }, { merge: true });
    }
    try {
      await setDoc(
        doc(firestore, FIRESTORE_COLLECTIONS.eventChats, resolvedEventId, 'participants', userId),
        buildEventParticipantPayload(currentUser),
        { merge: true },
      );
    } catch (error) {
      // A failed participant write must not leave a brand-new half-join behind.
      if (!wasAlreadyMember) {
        await setDoc(chatRef, { memberIds: arrayRemove(userId) }, { merge: true }).catch(() => {});
      }
      throw error;
    }
    if (!wasAlreadyMember) await postEventChatMemberNotice(resolvedEventId, userId, 'joined', currentUser.name);
  } catch (error) {
    console.warn('[Tonyte][RN][EventChat] join failed', {
      path,
      action: 'getDoc/setDoc',
      activityId: activity.id,
      resolvedEventId,
      userId,
      error,
    });
    throw error;
  }
}

export async function ensureInterestedAndJoinEventChat(
  activity: TonyteActivity,
  userId: string,
  _isAlreadyInterested: boolean,
  translate?: Translate,
) {
  assertSocialWriteAllowed();
  // Check expiry before persisting interest. An ended event must not recreate
  // its deleted chat or change the user's interest merely because Chat was tapped.
  if (isEventExpired(activity)) throw new EventChatExpiredError();
  // This idempotent write is deliberately unconditional: caller state can be
  // stale, and joining must never create membership without persisted interest.
  // It also safely normalizes legacy chat members whose interest record is missing.
  await setActivityInterested(activity, userId, true, translate);
  await joinEventChat(activity, userId);
}

// ── External registration (Luma) ───────────────────────────────────────────
// Partners like InterLokal take registrations — and count their limited
// spots — on Luma. Tonyte's interest options (interested / calendar / chat)
// therefore send people to the event's Luma page first and continue the
// in-app step once they're back.

const LUMA_EVENT_URL = /^https?:\/\/(www\.)?(luma\.com|lu\.ma)\/[^\s]+/i;

export function getLumaRegistrationUrl(activity: TonyteActivity): string | null {
  const candidates = [activity.sourceLinks?.eventPage, activity.officialLink];
  for (const candidate of candidates) {
    const url = typeof candidate === 'string' ? candidate.trim() : '';
    if (LUMA_EVENT_URL.test(url)) return url;
  }
  return null;
}

// Resolves when the user comes back to Tonyte after Linking.openURL left it.
// If the app never went to the background (e.g. web opened a new tab in the
// same window state), continue after a short grace period instead of waiting.
function waitForReturnToApp(leaveGraceMs = 2000): Promise<void> {
  return new Promise(resolve => {
    let left = AppState.currentState !== 'active';
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      subscription.remove();
      clearTimeout(timer);
      resolve();
    };
    const subscription = AppState.addEventListener('change', state => {
      if (state !== 'active') left = true;
      else if (left) finish();
    });
    const timer = setTimeout(() => { if (!left) finish(); }, leaveGraceMs);
  });
}

/**
 * Runs `next` (the chosen in-app action) — after first sending the user to
 * the event's Luma registration page, when it has one.
 */
export async function registerOnLumaThen(
  activity: TonyteActivity,
  next: () => unknown,
  translate?: Translate,
): Promise<void> {
  const url = getLumaRegistrationUrl(activity);
  if (url) {
    try {
      await Linking.openURL(url);
      await waitForReturnToApp();
    } catch {
      showTonyteToast(
        translateOr(translate, 'event.couldNotOpenRegistration', 'Could not open the registration page'),
        'error',
        url,
      );
    }
  }
  await next();
}

export const hasCalendarDate = (activity: TonyteActivity) => Boolean(activity.startDate || activity.startTime);

/** Opens the event in Google Calendar (the user confirms the save there). */
export async function openActivityInGoogleCalendar(activity: TonyteActivity, translate?: Translate) {
  try {
    await Linking.openURL(buildGoogleCalendarUrl(activity));
    trackEventCalendarAdd({ eventId: getResolvedEventId(activity), organizerId: activity.hostId });
    showTonyteToast(
      translateOr(translate, 'event.openingGoogleCalendar', 'Opening Google Calendar...'),
      'success',
      translateOr(translate, 'event.confirmCalendarSave', 'Confirm there to save the event.'),
    );
  } catch {
    showTonyteToast(
      translateOr(translate, 'event.couldNotOpenCalendar', 'Could not open calendar'),
      'error',
      translateOr(translate, 'common.tryAgain', 'Please try again.'),
    );
  }
}

/**
 * Same contract as ensureInterestedAndJoinEventChat: adding to calendar
 * always persists interest first, then opens the calendar. Returns true once
 * interest is saved so the caller can sync its local interested state/count
 * (even if opening the calendar itself then fails).
 */
export async function ensureInterestedAndAddToCalendar(
  activity: TonyteActivity,
  userId: string,
  translate?: Translate,
): Promise<boolean> {
  if (!hasCalendarDate(activity)) {
    showTonyteToast(
      translateOr(translate, 'event.noDateAvailable', 'No date available'),
      'error',
      translateOr(translate, 'event.noScheduledTime', 'This event does not have a scheduled time yet.'),
    );
    return false;
  }
  if (!requireVerifiedEmailForAction()) return false;
  try {
    // Shows its own alert on failure.
    await setActivityInterested(activity, userId, true, translate);
  } catch {
    return false;
  }
  await openActivityInGoogleCalendar(activity, translate);
  return true;
}

export async function deleteEventFromFirestore(activity: TonyteActivity, deletedBy: string) {
  const deletedPath = `${FIRESTORE_COLLECTIONS.deletedActivities}/${activity.id}`;
  try {
    await setDoc(doc(firestore, FIRESTORE_COLLECTIONS.deletedActivities, activity.id), {
      activityId: activity.id,
      title: activity.title,
      deletedBy,
      deletedAt: serverTimestamp(),
    }, { merge: true });
  } catch (error) {
    console.warn('[Tonyte][RN][EventDelete] deleted activity marker failed', {
      path: deletedPath,
      action: 'setDoc',
      error,
    });
  }

  const deleteResults = await Promise.allSettled(
    PUBLIC_ACTIVITY_COLLECTIONS.map(async collectionName => {
      const path = `${collectionName}/${activity.id}`;
      const ref = doc(firestore, collectionName, activity.id);
      const snapshot = await getDoc(ref);
      if (!snapshot.exists()) return;
      await deleteDoc(ref);
      console.log('[Tonyte][RN][EventDelete] deleted event doc', { path, action: 'deleteDoc' });
    }),
  );

  const failed = deleteResults.find(result => result.status === 'rejected');
  if (failed?.status === 'rejected') {
    console.warn('[Tonyte][RN][EventDelete] delete failed', {
      paths: PUBLIC_ACTIVITY_COLLECTIONS.map(collectionName => `${collectionName}/${activity.id}`),
      action: 'getDoc/deleteDoc',
      error: failed.reason,
    });
    throw failed.reason;
  }
}
